// Lightweight, dependency-free form validation. Built around the actual
// fields PettoBook uses today — phone (multi-country E.164), email, weight,
// price, and currency-amount inputs. The goal is to:
//   1. block obvious garbage at input time (digits-only filtering)
//   2. enforce country-aware phone length so a 5-digit HK number doesn't
//      pass when 8 are required
//   3. expose a single `validatePhone()` + `formatPhoneDisplay()` API the
//      UI can call without pulling in libphonenumber-js (200KB +)
//
// Country phone rules table — covers the 8 countries we surface in the
// COUNTRY_CODES picker plus a sane fallback. Numbers are mobile/landline
// excluding the country code itself.

const PHONE_RULES = {
  // HK: 8 digits, mobile starts with 5/6/9, landline 2/3
  "+852": { min: 8, max: 8, label: "香港" },
  // Taiwan: mobile 09xxxxxxxx (10 digits), landline 7-10 digits
  "+886": { min: 8, max: 10, label: "台灣" },
  // Mainland China: 11-digit mobile (1xxxxxxxxxx), landline 7-11
  "+86":  { min: 7, max: 11, label: "中國" },
  // Singapore: 8 digits
  "+65":  { min: 8, max: 8,  label: "新加坡" },
  // Malaysia: 9-10 digits
  "+60":  { min: 9, max: 11, label: "馬來西亞" },
  // US/Canada: 10 digits (NANP)
  "+1":   { min: 10, max: 10, label: "美國/加拿大" },
  // UK: mobile 10 digits (after leading 0 stripped)
  "+44":  { min: 9, max: 10, label: "英國" },
  // Australia: 9 digits (after leading 0 stripped)
  "+61":  { min: 9, max: 9,  label: "澳洲" },
};

// Strip everything that isn't a digit. Used both for filtering input as
// the user types and for normalizing before storage. Returns "" when the
// input is null / undefined.
export function normalizePhoneDigits(input) {
  if (input == null) return "";
  return String(input).replace(/\D+/g, "");
}

// Smart-parse a phone string that may include a country code prefix
// (copy-pasted from WhatsApp / contact card: "+852 9123 4567", "+1
// (212) 555-1234", "00852 91234567"). Returns:
//   { digits, countryCode }
// — digits  : the LOCAL number with country code stripped, digits only
// — countryCode : the matched "+852"-style code, or null if no prefix.
// Caller can use countryCode to also flip the country-code dropdown.
export function parseCopiedPhone(input) {
  if (input == null) return { digits: "", countryCode: null };
  let s = String(input).trim();
  // Normalize "00" international prefix to "+"
  if (s.startsWith("00")) s = "+" + s.slice(2);
  // Plus-prefixed: try to match the LONGEST country code (e.g. "+852"
  // before "+85", "+1234" before "+1"). Order PHONE_RULES keys longest-first.
  if (s.startsWith("+")) {
    const codes = Object.keys(PHONE_RULES).sort((a, b) => b.length - a.length);
    const digitsRaw = "+" + normalizePhoneDigits(s);
    for (const cc of codes) {
      if (digitsRaw.startsWith(cc)) {
        return { digits: digitsRaw.slice(cc.length), countryCode: cc };
      }
    }
    // Unknown country code — drop the "+" and return rest as digits.
    return { digits: digitsRaw.slice(1), countryCode: null };
  }
  return { digits: normalizePhoneDigits(s), countryCode: null };
}

// Validate a phone number against the country code's expected length.
// Returns { valid: bool, error?: string, e164?: string } where `e164` is
// the canonical international form (e.g. "+85265730181") suitable for
// storage and outbound APIs (WhatsApp, SMS).
export function validatePhone(countryCode, raw) {
  const digits = normalizePhoneDigits(raw);
  if (!digits) return { valid: false, error: "請輸入電話號碼" };
  // Reject unknown country codes outright. The previous default {min:6,max:15}
  // accepted nearly anything when a typo'd code (e.g. "+8521") was passed,
  // letting malformed numbers reach SMS / WhatsApp APIs and fail there.
  // Force callers to pick from PHONE_RULES (the same set the UI exposes).
  if (!PHONE_RULES[countryCode]) {
    return { valid: false, error: "請選擇國家/地區" };
  }
  const rule = PHONE_RULES[countryCode];
  if (digits.length < rule.min) {
    return { valid: false, error: `電話號碼太短，${rule.label || "此地區"}需要 ${rule.min === rule.max ? rule.min : `${rule.min}–${rule.max}`} 位數字` };
  }
  if (digits.length > rule.max) {
    return { valid: false, error: `電話號碼太長，${rule.label || "此地區"}最多 ${rule.max} 位數字` };
  }
  // Country-specific shape checks for the most common providers.
  if (countryCode === "+852") {
    // HK mobile starts with 4/5/6/7/8/9; landline with 2/3. Reject 0/1.
    const first = digits[0];
    if (!/[2-9]/.test(first)) {
      return { valid: false, error: "香港電話號碼必須以 2-9 開首" };
    }
  }
  if (countryCode === "+1") {
    // NANP: first digit (area code) and 4th digit (exchange) must be 2-9.
    if (!/^[2-9]/.test(digits) || !/^.{3}[2-9]/.test(digits)) {
      return { valid: false, error: "美/加電話號碼區號及交換碼必須以 2-9 開首" };
    }
  }
  return { valid: true, e164: countryCode + digits };
}

// Pretty-print for display. Inserts a space at the half-way mark for
// readability (HK style: "9123 4567"; US: "212 555 1234"). Falls back to
// the raw digits when the country rule isn't known.
export function formatPhoneDisplay(countryCode, raw) {
  const digits = normalizePhoneDigits(raw);
  if (!digits) return "";
  if (countryCode === "+1" && digits.length === 10) {
    return digits.slice(0, 3) + " " + digits.slice(3, 6) + " " + digits.slice(6);
  }
  if (countryCode === "+852" && digits.length === 8) {
    return digits.slice(0, 4) + " " + digits.slice(4);
  }
  if (countryCode === "+86" && digits.length === 11) {
    return digits.slice(0, 3) + " " + digits.slice(3, 7) + " " + digits.slice(7);
  }
  if (countryCode === "+44" && digits.length === 10) {
    return digits.slice(0, 4) + " " + digits.slice(4);
  }
  // Generic: split in halves if 8+ digits
  if (digits.length >= 8) {
    const half = Math.ceil(digits.length / 2);
    return digits.slice(0, half) + " " + digits.slice(half);
  }
  return digits;
}

// Validate an email address. RFC 5322 in full is overkill; this catches
// the obvious cases: must have a local part, @, and a domain with at
// least one dot. Trailing/leading whitespace gets trimmed before the test.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function validateEmail(raw) {
  const s = String(raw || "").trim();
  if (!s) return { valid: false, error: "請輸入電郵地址" };
  if (s.length > 254) return { valid: false, error: "電郵地址過長" };
  if (!EMAIL_RE.test(s)) return { valid: false, error: "電郵格式不正確" };
  return { valid: true, value: s.toLowerCase() };
}

// Numeric-only filter for amount / weight / price inputs. Strips
// everything that isn't a digit or decimal point, AND caps to one decimal
// point so "1.2.3" → "1.23". Pass allowDecimal=false for whole-number-only
// fields (visit counts, stamp slots, etc.).
export function normalizeNumeric(input, { allowDecimal = true, max = null } = {}) {
  if (input == null) return "";
  let s = String(input);
  s = allowDecimal ? s.replace(/[^\d.]/g, "") : s.replace(/\D+/g, "");
  if (allowDecimal) {
    // Keep only the FIRST decimal point.
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    }
  }
  if (max != null) {
    const n = parseFloat(s);
    if (!isNaN(n) && n > max) s = String(max);
  }
  return s;
}

// Round a money amount DOWN to the nearest integer unit (cent for USD,
// dollar for HKD/JPY which we treat as integer currencies). Using
// Math.round mid-calculation causes the customer to be charged more than
// the displayed price in edge cases (e.g. 333 * 0.30 = 99.9 → 100 charged
// but 99 displayed). Always round payouts down, surcharges up via
// ceilCents when needed. The Math.max guard prevents -0 / NaN propagation.
export function floorCents(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.floor(v));
}
export function ceilCents(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.ceil(v));
}

// Validate a positive amount (currency / weight). Returns the parsed
// number on success.
export function validateAmount(raw, { min = 0, max = Infinity, allowZero = false, label = "金額" } = {}) {
  const s = String(raw || "").trim();
  if (!s) return { valid: false, error: `請輸入${label}` };
  const n = parseFloat(s);
  if (isNaN(n)) return { valid: false, error: `${label}必須為數字` };
  if (!allowZero && n <= 0) return { valid: false, error: `${label}必須大於 0` };
  if (n < min) return { valid: false, error: `${label}最少為 ${min}` };
  if (n > max) return { valid: false, error: `${label}最多為 ${max}` };
  return { valid: true, value: n };
}

// Reusable onChange wrapper for digit-only inputs (phone, OTP, slot
// numbers). Use as: `onChange={digitsOnly(setX)}`.
export function digitsOnly(setter) {
  return (e) => setter(normalizePhoneDigits(e?.target?.value ?? e));
}
